import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import {
    ShoppingBag, Package, Truck, X, MapPin, User, Phone, Hash,
    ChevronRight, Coins, Search, ExternalLink, CalendarDays, CheckCircle2, Clock,
} from "lucide-react";

const COMMISSION_PCT = 15;

const SHIP_STATUS = {
    awaiting_approval: { label: "Aguarda aprovação", color: "bg-gold-soft/20 text-gold-deep" },
    stock_confirmed: { label: "Stock confirmado", color: "bg-vineyard/15 text-vineyard" },
    preparing: { label: "Em preparação", color: "bg-wine/10 text-wine" },
    ready_to_ship: { label: "Pronto para envio", color: "bg-wine/15 text-wine" },
    label_generated: { label: "Etiqueta gerada", color: "bg-gold-soft/30 text-gold-deep" },
    picked_up: { label: "Recolhido", color: "bg-vineyard/15 text-vineyard" },
    in_transit: { label: "Em trânsito", color: "bg-wine-dark/10 text-wine-dark" },
    out_for_delivery: { label: "Saiu para entrega", color: "bg-gold-soft/30 text-gold-deep" },
    delivered: { label: "Entregue", color: "bg-vineyard text-cream" },
    returned: { label: "Devolvido", color: "bg-destructive/15 text-destructive" },
    cancelled: { label: "Cancelado", color: "bg-wine-dark/10 text-wine-dark/60" },
};

export default function VendorOrders() {
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [producer, setProducer] = useState(null);
    const [shipMap, setShipMap] = useState({});
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            const [ord, me, ships] = await Promise.all([
                api.get("/vendor/orders").catch(() => ({ data: [] })),
                api.get("/vendor/me").catch(() => ({ data: null })),
                api.get("/vendor/shipments").catch(() => ({ data: [] })),
            ]);
            setOrders(ord.data || []);
            setProducer(me.data);
            const map = {};
            (ships.data || []).forEach((s) => { map[s.order_id] = s; });
            setShipMap(map);
            setLoading(false);
        })();
    }, []);

    const pid = producer?.id;
    const myItems = (o) => (o.items || []).filter((it) => !pid || it.producer_id === pid);
    const myTotal = (o) => myItems(o).reduce((s, i) => s + i.subtotal, 0);
    const myUnits = (o) => myItems(o).reduce((s, i) => s + i.quantity, 0);

    const enriched = useMemo(() => orders.map((o) => {
        const ship = shipMap[o.id];
        return { ...o, _ship: ship, _status: ship?.status || "awaiting_approval", _total: myTotal(o), _units: myUnits(o), _items: myItems(o) };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [orders, shipMap, pid]);

    const filtered = enriched.filter((o) => {
        if (statusFilter !== "all" && o._status !== statusFilter) return false;
        if (!q) return true;
        const hay = `${o.id} ${o.tracking_code || ""} ${o.shipping_address?.full_name || ""} ${o.shipping_address?.city || ""}`.toLowerCase();
        return hay.includes(q.toLowerCase());
    });

    const totals = useMemo(() => {
        const net = enriched.reduce((s, o) => s + o._total, 0) * (1 - COMMISSION_PCT / 100);
        const units = enriched.reduce((s, o) => s + o._units, 0);
        const toShip = enriched.filter((o) => !["delivered", "cancelled", "returned"].includes(o._status)).length;
        return { revenue: net, units, toShip, count: enriched.length };
    }, [enriched]);

    const statusCounts = useMemo(() => {
        const c = {};
        enriched.forEach((o) => { c[o._status] = (c[o._status] || 0) + 1; });
        return c;
    }, [enriched]);

    return (
        <section className="space-y-6" data-testid="vendor-orders">
            <div>
                <span className="label-overline text-wine/60 inline-flex items-center gap-1.5">
                    <ShoppingBag className="h-3 w-3" /> Vendas
                </span>
                <h1 className="font-heading text-3xl text-wine-dark">Encomendas</h1>
                <p className="text-sm text-wine-dark/60 mt-1">Clique numa encomenda para ver os detalhes, itens, cliente e estado de envio.</p>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Kpi label="Encomendas" value={totals.count} icon={ShoppingBag} />
                <Kpi label="Unidades vendidas" value={totals.units} icon={Package} />
                <Kpi label="A enviar" value={totals.toShip} icon={Truck} warn={totals.toShip > 0} />
                <Kpi label="Receita líquida" value={`€${totals.revenue.toFixed(2)}`} icon={Coins} sub={`Após ${COMMISSION_PCT}% comissão`} highlight />
            </div>

            {/* Search + filters */}
            <div className="bg-white rounded-md border border-gold-soft/30 p-3 flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                    <Search className="h-4 w-4 text-wine-dark/40 ml-1" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Procurar por #pedido, cliente, cidade ou tracking…"
                        className="flex-1 bg-transparent text-sm focus:outline-none"
                        data-testid="orders-search"
                    />
                </div>
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="text-xs uppercase tracking-wider bg-cream border border-wine-dark/10 rounded-md px-3 py-2 text-wine-dark"
                    data-testid="orders-status-filter"
                >
                    <option value="all">Todos os estados ({enriched.length})</option>
                    {Object.entries(SHIP_STATUS).filter(([k]) => (statusCounts[k] || 0) > 0).map(([k, m]) => (
                        <option key={k} value={k}>{m.label} ({statusCounts[k]})</option>
                    ))}
                </select>
            </div>

            {/* Table */}
            <div className="bg-white rounded-md border border-gold-soft/30 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="text-left label-overline text-wine-dark/60 border-b border-wine-dark/10 bg-cream/40">
                                <th className="py-3 px-4">Pedido</th>
                                <th>Cliente</th>
                                <th className="text-center">Itens</th>
                                <th>Estado de envio</th>
                                <th>Tracking</th>
                                <th className="text-right pr-4">Total (meu)</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((o) => {
                                const meta = SHIP_STATUS[o._status] || SHIP_STATUS.awaiting_approval;
                                return (
                                    <tr
                                        key={o.id}
                                        onClick={() => setSelected(o)}
                                        className="border-t border-wine-dark/5 hover:bg-cream cursor-pointer transition-colors"
                                        data-testid={`order-row-${o.id}`}
                                    >
                                        <td className="py-3 px-4">
                                            <p className="font-mono text-xs text-wine-dark/80">#{o.id.slice(0, 8)}</p>
                                            <p className="text-[11px] text-wine-dark/50">{fmtDate(o.created_at)}</p>
                                        </td>
                                        <td className="text-xs text-wine-dark/70">
                                            {o.shipping_address?.full_name || "—"}<br />
                                            <span className="text-[10px] text-wine-dark/50">{o.shipping_address?.city || ""}</span>
                                        </td>
                                        <td className="text-center text-wine-dark/70">{o._units}</td>
                                        <td>
                                            <span className={`inline-flex items-center label-overline px-2 py-1 rounded text-[10px] ${meta.color}`}>
                                                {meta.label}
                                            </span>
                                        </td>
                                        <td className="text-xs">
                                            {o.tracking_code ? <span className="font-mono text-wine">{o.tracking_code}</span> : <span className="text-wine-dark/30">—</span>}
                                        </td>
                                        <td className="text-right pr-4 font-heading text-base text-wine-dark">€{o._total.toFixed(2)}</td>
                                        <td className="px-3 text-wine"><ChevronRight className="h-4 w-4" /></td>
                                    </tr>
                                );
                            })}
                            {!loading && filtered.length === 0 && (
                                <tr><td colSpan="7" className="text-center py-12 text-wine-dark/60">Sem encomendas neste filtro.</td></tr>
                            )}
                            {loading && (
                                <tr><td colSpan="7" className="text-center py-12 text-wine-dark/50 label-overline">A carregar…</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {selected && (
                <OrderDrawer
                    order={selected}
                    onClose={() => setSelected(null)}
                    onManageShipment={() => navigate("/vendor/envios")}
                />
            )}
        </section>
    );
}

function OrderDrawer({ order, onClose, onManageShipment }) {
    const meta = SHIP_STATUS[order._status] || SHIP_STATUS.awaiting_approval;
    const subtotal = order._total;
    const commission = subtotal * COMMISSION_PCT / 100;
    const net = subtotal - commission;
    const addr = order.shipping_address || {};

    return (
        <div className="fixed inset-0 z-50 flex items-stretch justify-end bg-elegant/40 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-cream w-full max-w-2xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="order-drawer">
                {/* Header */}
                <div className="sticky top-0 z-10 bg-cream border-b border-wine-dark/10 px-8 py-5 flex items-start justify-between">
                    <div>
                        <span className="label-overline text-wine inline-flex items-center gap-1.5"><ShoppingBag className="h-3 w-3" /> Encomenda</span>
                        <h2 className="font-heading text-3xl text-wine-dark mt-1">#{order.id.slice(0, 8)}</h2>
                        <div className="flex items-center gap-2 mt-2">
                            <span className="inline-flex items-center gap-1 bg-vineyard/15 text-vineyard label-overline px-2 py-1 rounded text-[10px]">
                                <CheckCircle2 className="h-3 w-3" /> Paga
                            </span>
                            <span className={`inline-flex items-center label-overline px-2 py-1 rounded text-[10px] ${meta.color}`}>{meta.label}</span>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-wine-dark/60 hover:text-wine p-2" data-testid="order-drawer-close"><X className="h-5 w-5" /></button>
                </div>

                <div className="p-8 space-y-5">
                    {/* Meta line */}
                    <div className="flex flex-wrap gap-6 text-xs text-wine-dark/60">
                        <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5 text-wine/60" /> {fmtDateTime(order.created_at)}</span>
                        {order.tracking_code && <span className="inline-flex items-center gap-1.5"><Hash className="h-3.5 w-3.5 text-wine/60" /> <span className="font-mono">{order.tracking_code}</span></span>}
                    </div>

                    {/* Customer + address */}
                    <Block title="Cliente & entrega">
                        <p className="text-sm text-wine-dark flex items-center gap-2"><User className="h-3.5 w-3.5 text-wine/60" /> {addr.full_name || "—"}</p>
                        {addr.phone && <p className="text-xs text-wine-dark/65 flex items-center gap-2 mt-1"><Phone className="h-3.5 w-3.5 text-wine/60" /> {addr.phone}</p>}
                        <div className="mt-2 pt-2 border-t border-wine-dark/5 text-xs text-wine-dark/65 flex items-start gap-2">
                            <MapPin className="h-3.5 w-3.5 text-wine/60 mt-0.5" />
                            <span>
                                {addr.street}<br />
                                {addr.postal_code} {addr.city}{addr.country ? ` · ${addr.country}` : ""}
                            </span>
                        </div>
                    </Block>

                    {/* My items */}
                    <Block title={`Os meus itens (${order._items.length})`}>
                        {order._items.map((it, i) => (
                            <div key={`${it.product_id}-${i}`} className="flex justify-between items-center py-2 border-b border-wine-dark/5 last:border-0">
                                <div>
                                    <p className="text-sm text-wine-dark">{it.name}</p>
                                    <p className="text-xs text-wine-dark/55">{it.quantity} × €{it.price.toFixed(2)}</p>
                                </div>
                                <span className="font-heading text-base text-wine-dark">€{it.subtotal.toFixed(2)}</span>
                            </div>
                        ))}
                    </Block>

                    {/* Financial summary */}
                    <Block title="Resumo financeiro">
                        <Row label="Subtotal dos meus itens" value={`€${subtotal.toFixed(2)}`} />
                        <Row label={`Comissão plataforma (${COMMISSION_PCT}%)`} value={`−€${commission.toFixed(2)}`} muted />
                        <div className="flex justify-between items-center pt-3 mt-2 border-t border-wine-dark/10">
                            <span className="label-overline text-vineyard">A receber (líquido)</span>
                            <span className="font-heading text-2xl text-vineyard">€{net.toFixed(2)}</span>
                        </div>
                    </Block>

                    {/* Logistics link */}
                    <div className="bg-white border border-gold-soft/40 rounded-md p-4 flex items-center justify-between">
                        <div>
                            <span className="label-overline text-wine flex items-center gap-2"><Truck className="h-3.5 w-3.5" /> Logística</span>
                            <p className="text-xs text-wine-dark/60 mt-1">
                                {order.tracking_code
                                    ? "Tracking já adicionado. Atualize o estado em Envios."
                                    : "Adicione tracking e gira o estado do envio na secção Envios."}
                            </p>
                        </div>
                        <button onClick={onManageShipment} className="btn-wine text-xs inline-flex items-center gap-1.5 whitespace-nowrap" data-testid="order-manage-shipment">
                            Gerir envio <ExternalLink className="h-3.5 w-3.5" />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Kpi({ label, value, icon: Icon, sub, highlight, warn }) {
    return (
        <div className={`p-4 rounded-md border ${highlight ? "bg-wine-dark text-cream border-wine-dark" : warn ? "bg-gold-soft/10 border-gold-soft/40" : "bg-white border-gold-soft/30"}`}>
            <div className="flex items-center justify-between">
                <span className={`label-overline ${highlight ? "text-gold-soft" : "text-wine/60"}`}>{label}</span>
                <Icon className={`h-4 w-4 ${highlight ? "text-gold-soft" : "text-wine/40"}`} strokeWidth={1.5} />
            </div>
            <p className="font-heading text-2xl mt-1">{value}</p>
            {sub && <p className={`text-[10px] mt-0.5 ${highlight ? "text-cream/60" : "text-wine-dark/45"}`}>{sub}</p>}
        </div>
    );
}

function Block({ title, children }) {
    return (
        <div className="bg-white border border-gold-soft/30 rounded-md p-4">
            <h3 className="label-overline text-wine mb-3">{title}</h3>
            {children}
        </div>
    );
}

function Row({ label, value, muted }) {
    return (
        <div className="flex justify-between items-center py-1">
            <span className={`text-sm ${muted ? "text-wine-dark/55" : "text-wine-dark/75"}`}>{label}</span>
            <span className={`text-sm ${muted ? "text-gold-deep" : "text-wine-dark font-semibold"}`}>{value}</span>
        </div>
    );
}

function fmtDate(s) {
    try { return new Date(s).toLocaleDateString("pt-PT"); } catch { return "—"; }
}
function fmtDateTime(s) {
    try { return new Date(s).toLocaleString("pt-PT"); } catch { return "—"; }
}
